import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { colors } from '../../const/colors'
import { IParkingPoolAvailabilityDay } from '../../graphql/parkingPool/getParkingPoolAvailability'
import { StepTitle, SectionLabel } from './reserveOnBehalfUi'
import {
  DayState,
  LEGEND_STATES,
  dayState,
  hasUserReservationLimit,
  reasonKey,
} from './reservationDayState'

const pad = (n: number) => String(n).padStart(2, '0')
const dayKey = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)
const endOfMonth = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)
const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7

const stateColors: Record<
  DayState,
  { color: string; background: string; dot: string }
> = {
  booked: { color: colors.red, background: colors.redBg, dot: colors.red },
  full: {
    color: colors.amber,
    background: colors.amberBg,
    dot: colors.amberMark,
  },
  blocked: {
    color: colors.slateLight,
    background: colors.bg,
    dot: colors.slateLight,
  },
}

const navBtnSx = {
  width: 32,
  height: 32,
  borderRadius: '9px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.navy,
  cursor: 'pointer',
  '&:hover': { background: '#EFF3F6' },
}

const ReservationCalendar: FC<{
  availabilityDays: IParkingPoolAvailabilityDay[]
  loading: boolean
  maxDays?: number
  maxReservationsPerUser?: number
  bookedRanges?: { from: string; to: string }[]
  selected: string[]
  onSelectedChange: (next: string[]) => void
  onMonthChange: (from: string, to: string) => void
}> = ({
  availabilityDays,
  loading,
  maxDays,
  maxReservationsPerUser,
  bookedRanges,
  selected,
  onSelectedChange,
  onMonthChange,
}) => {
  const { t, i18n } = useTranslation(['user_details', 'days'])
  const WEEKDAYS = [
    t('days:Monday'),
    t('days:Tuesday'),
    t('days:Wednesday'),
    t('days:Thursday'),
    t('days:Friday'),
    t('days:Saturday'),
    t('days:Sunday'),
  ]

  const [viewDate, setViewDate] = useState(() => startOfMonth(new Date()))
  const [mode, setMode] = useState<'single' | 'range'>('single')
  const [rangeStart, setRangeStart] = useState<string | null>(null)
  const [hoverKey, setHoverKey] = useState<string | null>(null)
  const [skipped, setSkipped] = useState(0)

  useEffect(() => {
    onMonthChange(
      startOfMonth(viewDate).toISOString(),
      endOfMonth(viewDate).toISOString(),
    )
  }, [viewDate, onMonthChange])

  const availByKey = useMemo(() => {
    const map: Record<string, IParkingPoolAvailabilityDay> = {}
    availabilityDays.forEach(d => {
      map[d.date.slice(0, 10)] = d
    })
    return map
  }, [availabilityDays])

  const selectedKeys = useMemo(
    () => new Set(selected.map(s => s.slice(0, 10))),
    [selected],
  )

  const today = useMemo(startOfToday, [])

  const isBooked = (key: string) =>
    !!bookedRanges?.some(r => key >= r.from && key <= r.to)

  const toggleDay = (date: Date) => {
    const key = dayKey(date)
    const avail = availByKey[key]
    if (!avail || !avail.available) return
    const has = selected.some(s => s.slice(0, 10) === key)
    if (has) {
      onSelectedChange(selected.filter(s => s.slice(0, 10) !== key))
      return
    }
    if (maxDays && selected.length >= maxDays) return
    onSelectedChange([...selected, avail.date].sort())
  }

  const keysBetween = (a: string, b: string) => {
    const [from, to] = a <= b ? [a, b] : [b, a]
    const keys: string[] = []
    const cursor = new Date(`${from}T00:00:00`)
    const last = new Date(`${to}T00:00:00`)
    while (cursor <= last) {
      keys.push(dayKey(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    return keys
  }

  const addKeys = (span: string[]) => {
    const free = span.filter(k => availByKey[k]?.available)
    const room = maxDays ? Math.max(0, maxDays - selected.length) : Infinity
    const added = free
      .filter(k => !selectedKeys.has(k))
      .slice(0, room)
      .map(k => availByKey[k].date)

    setSkipped(span.length - free.length)
    if (added.length) onSelectedChange([...selected, ...added].sort())
  }

  const applyRange = (startKey: string, endKey: string) =>
    addKeys(keysBetween(startKey, endKey))

  const monthKeys = useMemo(() => {
    const cursor = startOfMonth(viewDate)
    const last = endOfMonth(viewDate)
    const keys: string[] = []
    while (cursor <= last) {
      const key = dayKey(cursor)
      if (cursor >= today && availByKey[key]?.available) keys.push(key)
      cursor.setDate(cursor.getDate() + 1)
    }
    return keys
  }, [viewDate, availByKey, today])

  const monthPicked =
    !!monthKeys.length && monthKeys.every(key => selectedKeys.has(key))

  const toggleMonth = () => {
    setRangeStart(null)
    if (monthPicked) {
      setSkipped(0)
      onSelectedChange(
        selected.filter(s => !monthKeys.includes(s.slice(0, 10))),
      )
      return
    }
    const first = startOfMonth(viewDate)
    const last = endOfMonth(viewDate)
    const from = first < today ? today : first
    addKeys(keysBetween(dayKey(from), dayKey(last)))
  }

  const handleDayClick = (date: Date) => {
    const key = dayKey(date)
    if (loading || !availByKey[key]?.available) return
    if (mode === 'single') {
      setSkipped(0)
      toggleDay(date)
      return
    }
    if (!rangeStart) {
      setSkipped(0)
      setRangeStart(key)
      return
    }
    applyRange(rangeStart, key)
    setRangeStart(null)
  }

  const previewKeys = useMemo(() => {
    if (mode !== 'range' || !rangeStart) return new Set<string>()
    return new Set(keysBetween(rangeStart, hoverKey ?? rangeStart))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rangeStart, hoverKey])

  const clearAll = () => {
    setRangeStart(null)
    setSkipped(0)
    onSelectedChange([])
  }

  const removeRange = (from: Date, to: Date) => {
    setSkipped(0)
    onSelectedChange(
      selected.filter(s => {
        const time = new Date(`${s.slice(0, 10)}T00:00:00`).getTime()
        return time < from.getTime() || time > to.getTime()
      }),
    )
  }

  const weeks = useMemo(() => {
    const first = startOfMonth(viewDate)
    const daysInMonth = endOfMonth(viewDate).getDate()
    const offset = mondayIndex(first)
    const cells: (Date | null)[] = []
    for (let i = 0; i < offset; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++)
      cells.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), d))
    while (cells.length % 7 !== 0) cells.push(null)
    const rows: (Date | null)[][] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [viewDate])

  const monthLabel = useMemo(
    () =>
      viewDate.toLocaleDateString(i18n.language, {
        month: 'long',
        year: 'numeric',
      }),
    [viewDate, i18n.language],
  )

  const dayCellSx = (date: Date | null) => {
    if (date === null) return { height: 42 }
    const base = {
      height: 42,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '15px',
      fontWeight: 700,
      borderRadius: '11px',
    }
    const key = dayKey(date)
    const avail = availByKey[key]
    const isPast = date < today
    const isSelected = selectedKeys.has(key)
    if (isPast) return { ...base, color: colors.borderCheck, cursor: 'default' }
    if (!avail || !avail.available) {
      const { color, background } =
        stateColors[dayState(avail?.reasonCode, isBooked(key))]
      return { ...base, color, background, cursor: 'not-allowed' }
    }
    const isRangeStart = key === rangeStart
    const isPreview = previewKeys.has(key)
    if (isSelected)
      return {
        ...base,
        color: colors.navy,
        background: 'rgba(79,143,82,0.5)',
        cursor: 'pointer',
        ...(isRangeStart && { boxShadow: `inset 0 0 0 2px ${colors.navy}` }),
      }
    if (isRangeStart)
      return {
        ...base,
        color: colors.navy,
        background: 'rgba(79,143,82,0.25)',
        boxShadow: `inset 0 0 0 2px ${colors.navy}`,
        cursor: 'pointer',
      }
    if (isPreview)
      return {
        ...base,
        color: colors.navy,
        background: 'rgba(79,143,82,0.18)',
        cursor: 'pointer',
      }
    return {
      ...base,
      color: colors.navy,
      cursor: 'pointer',
      '&:hover': { background: colors.rowHover },
    }
  }

  const chips = useMemo(() => {
    const dates = selected
      .map(s => new Date(s.slice(0, 10)))
      .sort((a, b) => a.getTime() - b.getTime())
    const ranges: [Date, Date][] = []
    let start: Date | null = null
    let prev: Date | null = null
    for (const d of dates) {
      if (!start || !prev) {
        start = d
        prev = d
      } else if (d.getTime() - prev.getTime() === 86400000) {
        prev = d
      } else {
        ranges.push([start, prev])
        start = d
        prev = d
      }
    }
    if (start && prev) ranges.push([start, prev])
    return ranges.map(([a, b]) => {
      const cnt = Math.round((b.getTime() - a.getTime()) / 86400000) + 1
      const fmt = (d: Date) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`
      const label = a.getTime() === b.getTime() ? fmt(a) : `${fmt(a)}–${fmt(b)}`
      return {
        label: `${label} · ${t('reserve_modal.days', { count: cnt })}`,
        from: a,
        to: b,
      }
    })
  }, [selected, t])

  const limitReached = useMemo(
    () => hasUserReservationLimit(availabilityDays),
    [availabilityDays],
  )

  return (
    <Box>
      <StepTitle
        n="1"
        title={t('reserve_modal.step1_title')}
        sub={t('reserve_modal.step1_sub')}
      />

      {limitReached && (
        <Box
          data-e2e="user-limit-reached"
          sx={{
            mb: '12px',
            p: '10px 14px',
            borderRadius: '11px',
            background: colors.redBg,
            color: colors.red,
            fontSize: '12.5px',
            fontWeight: 700,
          }}
        >
          {maxReservationsPerUser
            ? t('reserve_modal.user_limit_reached_count', {
                count: maxReservationsPerUser,
              })
            : t('reserve_modal.user_limit_reached')}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          mb: '12px',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: '3px',
            borderRadius: '11px',
            background: colors.bg,
            border: `1px solid ${colors.border}`,
          }}
        >
          {(['single', 'range'] as const).map(option => (
            <Box
              key={option}
              onClick={() => {
                setMode(option)
                setRangeStart(null)
                setSkipped(0)
              }}
              sx={{
                px: '14px',
                height: 32,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '9px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'background .15s, color .15s',
                ...(mode === option
                  ? {
                      background: colors.surface,
                      color: colors.navy,
                      boxShadow: colors.shadowCard,
                    }
                  : { color: colors.textMuted }),
              }}
            >
              {t(
                option === 'single'
                  ? 'reserve_modal.mode_single'
                  : 'reserve_modal.mode_range',
              )}
            </Box>
          ))}
        </Box>
        <Box
          onClick={() => !loading && monthKeys.length && toggleMonth()}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: 38,
            px: '14px',
            borderRadius: '11px',
            border: `1.5px solid ${monthPicked ? colors.navy : colors.border3}`,
            background: monthPicked ? colors.rowSelected : colors.surface,
            color: monthKeys.length ? colors.navy : colors.slateLight,
            fontSize: '12.5px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            cursor: monthKeys.length ? 'pointer' : 'not-allowed',
            transition: 'border-color .15s, background .15s',
            '&:hover': monthKeys.length
              ? { borderColor: colors.navy }
              : undefined,
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: 15 }} />
          {monthPicked
            ? t('reserve_modal.unselect_month')
            : t('reserve_modal.select_month', { n: monthKeys.length })}
        </Box>
        {mode === 'range' && (
          <Typography
            sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}
          >
            {rangeStart
              ? t('reserve_modal.range_hint_end')
              : t('reserve_modal.range_hint_start')}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          p: '16px',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '12px',
          }}
        >
          <Box
            sx={navBtnSx}
            onClick={() =>
              setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))
            }
          >
            <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            sx={{
              fontSize: '15.5px',
              fontWeight: 800,
              textTransform: 'capitalize',
            }}
          >
            {monthLabel}
          </Typography>
          <Box
            sx={navBtnSx}
            data-e2e="calendar-next-month"
            onClick={() =>
              setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))
            }
          >
            <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7,1fr)',
            gap: '4px',
            mb: '4px',
          }}
        >
          {WEEKDAYS.map(wd => (
            <Box
              key={wd}
              sx={{
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 700,
                color: colors.slateLight,
              }}
            >
              {wd.slice(0, 3)}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {weeks.map((week, wi) => (
            <Box
              key={wi}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7,1fr)',
                gap: '4px',
              }}
            >
              {week.map((date, di) => {
                const key = date && dayKey(date)
                const avail = key ? availByKey[key] : undefined
                const unavailable = !!date && date >= today && !avail?.available
                const state = unavailable
                  ? dayState(avail?.reasonCode, isBooked(key as string))
                  : null
                const cell = (
                  <Box
                    key={di}
                    data-e2e={key ? `calendar-day-${key}` : undefined}
                    onClick={() => date !== null && handleDayClick(date)}
                    onMouseEnter={() =>
                      date !== null && setHoverKey(dayKey(date))
                    }
                    onMouseLeave={() => setHoverKey(null)}
                    sx={dayCellSx(date)}
                  >
                    {date?.getDate() ?? ''}
                  </Box>
                )
                return state ? (
                  <Tooltip
                    key={di}
                    title={t(reasonKey(avail?.reasonCode, state), {
                      defaultValue:
                        avail?.reason ?? t('reserve_modal.reason_unknown'),
                    })}
                    placement="top"
                  >
                    {cell}
                  </Tooltip>
                ) : (
                  cell
                )
              })}
            </Box>
          ))}
        </Box>

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.6)',
              borderRadius: '16px',
            }}
          >
            <CircularProgress size={24} sx={{ color: colors.navy }} />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: '16px',
            rowGap: '7px',
            mt: '14px',
            pt: '14px',
            borderTop: '1px solid #EFF3F6',
            fontSize: '12.5px',
            color: colors.textSecondary,
            fontWeight: 600,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'rgba(79,143,82,0.5)',
              }}
            />
            {t('reserve_modal.legend_selected')}
          </Box>
          {LEGEND_STATES.map(state => (
            <Box
              key={state}
              sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: stateColors[state].dot,
                }}
              />
              {t(`reserve_modal.legend_${state}`)}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '12px',
          mt: '18px',
        }}
      >
        <SectionLabel>{t('reserve_modal.selected_dates')}</SectionLabel>
        {!!selected.length && (
          <Typography
            onClick={clearAll}
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: colors.textMuted,
              cursor: 'pointer',
              flexShrink: 0,
              '&:hover': { color: colors.red },
            }}
          >
            {t('reserve_modal.clear_all')}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '9px' }}>
        {chips.length ? (
          chips.map(chip => (
            <Box
              key={chip.label}
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: colors.greenBg,
                color: colors.greenBgText,
                fontSize: '13.5px',
                fontWeight: 700,
                p: '9px 10px 9px 14px',
                borderRadius: '11px',
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 15 }} />
              {chip.label}
              <Box
                onClick={() => removeRange(chip.from, chip.to)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: colors.greenBgText,
                  transition: 'background .15s, color .15s',
                  '&:hover': { background: colors.redBg, color: colors.red },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: '13px', color: colors.slate }}>
            {t('reserve_modal.no_days')}
          </Typography>
        )}
      </Box>
      {skipped > 0 && (
        <Typography
          sx={{ fontSize: '12px', color: colors.textMuted, mt: '8px' }}
        >
          {t('reserve_modal.range_skipped', { count: skipped })}
        </Typography>
      )}
      {!!maxDays && selected.length >= maxDays && (
        <Typography sx={{ fontSize: '12px', color: colors.red, mt: '8px' }}>
          {t('reserve_modal.max_days_reached', { count: maxDays })}
        </Typography>
      )}
    </Box>
  )
}

export default ReservationCalendar
